import { GetFilesResponseType } from "@/app/[locale]/(platform)/storage/hooks/useGetFiles";
import { GetFoldersResponseType } from "@/app/[locale]/(platform)/storage/hooks/useGetFolders";
import { StorageFilterByType } from "@/app/[locale]/(platform)/storage/types";
import { LucideIcon } from "lucide-react";
import { create } from "zustand";

export type FolderClickActions = "navigate-to-folder" | "select-folder";
export type FileClickActions = "navigate-to-file" | "select-file";

export type StorageExplorerSettings = {
    description?: string;
    actionsButton?: {
        createFolder?: boolean;
        uploadVideos?: boolean;
        storageSettings?: boolean;
    };
    folders: {
        initialParentFolder?: GetFoldersResponseType["data"][0];
        initialSelectedFolders?: GetFoldersResponseType["data"];

        onFolderClickActions: FolderClickActions[];

        menuOptions?: {
            select?: boolean;
            settings?: boolean;
            delete?: boolean;
            exportEmbedLinks?: boolean;
            exportSubtitles?: boolean;
        };
    };
    files?: {
        initialSelectedFiles?: GetFilesResponseType["data"];
        initialFilterBy: StorageFilterByType;

        onFileClickActions: FileClickActions[];

        menuOptions?: {
            select?: boolean;
        };
    };
    selectedItemsMenu: {
        mode: "single-file" | "single-folder" | "multiple-items";
        buttons?: {
            move?: boolean;
            cancel?: boolean;
            custom?: {
                text: string;
                icon: LucideIcon;
                onClick: (
                    selectedFolders: GetFoldersResponseType["data"],
                    selectedFiles: GetFilesResponseType["data"]
                ) => void;
            };
        };
    };
};
export const defaultStorageExplorerSettings: StorageExplorerSettings = {
    actionsButton: {
        createFolder: true,
        storageSettings: true,
        uploadVideos: true,
    },
    folders: {
        onFolderClickActions: ["navigate-to-folder"],
        menuOptions: {
            select: true,
            settings: true,
            delete: true,
            exportEmbedLinks: true,
            exportSubtitles: true,
        },
    },
    files: {
        onFileClickActions: ["navigate-to-file"],
        initialFilterBy: "all",
        menuOptions: {
            select: true,
        },
    },
    selectedItemsMenu: {
        mode: "multiple-items",
        buttons: {
            move: true,
            cancel: true,
        },
    },
};

export type VideoOptions = {
    videoId: string;
    libraryId: number;
    uid: string;
};
export type PresentationOptions = {
    presentationId: string;
    libraryId: number;
};

export type LomdaOptions = {
    lomdaId: string;
    libraryId: number;
};

export type LomdaV2Options = {
    lomdaId: string;
    libraryId: number;
};

export type LomdaV3Options = {
    lomdaId: string;
    libraryId: number;
};

interface StorageStore {
    explorerOpen: boolean;
    setExplorerOpen: (
        open: boolean,
        settings?: StorageExplorerSettings
    ) => void;
    explorerSettings: StorageExplorerSettings;

    videoSheetOpen: boolean;
    setVideoSheetOpen: (open: boolean, options?: VideoOptions) => void;
    videoOptions: VideoOptions;

    presentationSheetOpen: boolean;
    setPresentationSheetOpen: (
        open: boolean,
        options?: PresentationOptions
    ) => void;
    presentationOptions: PresentationOptions;

    lomdaSheetOpen: boolean;
    setLomdaSheetOpen: (open: boolean, options?: LomdaOptions) => void;
    lomdaOptions: LomdaOptions;

    lomdaV2SheetOpen: boolean;
    setLomdaV2SheetOpen: (open: boolean, options?: LomdaV2Options) => void;
    lomdaV2Options: LomdaV2Options;

    lomdaV3SheetOpen: boolean;
    setLomdaV3SheetOpen: (open: boolean, options?: LomdaV3Options) => void;
    lomdaV3Options: LomdaV3Options;
}

export const useStorage = create<StorageStore>((set, get) => ({
    explorerOpen: false,
    setExplorerOpen: (open: boolean, settings?: StorageExplorerSettings) => {
        set({
            explorerOpen: open,
            ...(settings && { explorerSettings: settings }),
        });
    },
    explorerSettings: defaultStorageExplorerSettings,

    videoSheetOpen: false,
    setVideoSheetOpen: (open: boolean, options?: VideoOptions) => {
        set({
            videoSheetOpen: open,
            ...(options && { videoOptions: options }),
        });
    },
    videoOptions: {
        videoId: "",
        libraryId: 0,
        uid: "",
    },

    presentationSheetOpen: false,
    setPresentationSheetOpen: (
        open: boolean,
        options?: PresentationOptions
    ) => {
        set({
            presentationSheetOpen: open,
            ...(options && { presentationOptions: options }),
        });
    },
    presentationOptions: {
        presentationId: "",
        libraryId: 0,
    },

    lomdaSheetOpen: false,
    setLomdaSheetOpen: (open: boolean, options?: LomdaOptions) => {
        set({
            lomdaSheetOpen: open,
            ...(options && { lomdaOptions: options }),
        });
    },
    lomdaOptions: {
        lomdaId: "",
        libraryId: 0,
    },

    lomdaV2SheetOpen: false,
    setLomdaV2SheetOpen: (open: boolean, options?: LomdaV2Options) => {
        set({
            lomdaV2SheetOpen: open,
            ...(options && { lomdaV2Options: options }),
        });
    },
    lomdaV2Options: {
        lomdaId: "",
        libraryId: 0,
    },

    lomdaV3SheetOpen: false,
    setLomdaV3SheetOpen: (open: boolean, options?: LomdaV3Options) => {
        set({
            lomdaV3SheetOpen: open,
            ...(options && { lomdaV3Options: options }),
        });
    },
    lomdaV3Options: {
        lomdaId: "",
        libraryId: 0,
    },
}));
